import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { NotificacionesService } from '../ui/notificaciones.service';
import { SIN_NOTIFICAR_ERROR } from './contexto';

/**
 * Un mensaje del backend que es un volcado técnico (la base, la red, un
 * plazo vencido) y no una frase para el usuario. Ése no se muestra: va a la
 * consola y el usuario ve el mensaje genérico de su estado HTTP.
 */
const TECNICO =
  /mongo|connection\(|context deadline|timed out|i\/o timeout|\btcp\b|ECONN|ETIMEDOUT|socket|stack|\bat [\w.]+ \(|\[-\d+\]|Cast to \w+ failed|ObjectId|E11000|ValidationError|\bundefined\b/i;

/**
 * Un id que no tiene forma de id ("/inventario/luminarias/xyz"): Mongo responde
 * "Cast to ObjectId failed for value…", que no le dice nada al usuario. Para él
 * es lo mismo que un id que no existe.
 */
const ID_INVALIDO = /Cast to ObjectId failed/i;

function esTecnico(texto: string): boolean {
  return TECNICO.test(texto) || texto.length > 240;
}

/** Traduce el estado HTTP a algo que le sirva a quien está trabajando. */
function mensajeDe(error: HttpErrorResponse): string {
  const crudo = (error.error as { message?: unknown } | null)?.message;
  const delBackend = Array.isArray(crudo)
    ? crudo.join(' · ')
    : typeof crudo === 'string'
      ? crudo
      : '';
  if (ID_INVALIDO.test(delBackend)) {
    return 'No se encontró lo que se pidió: la dirección no corresponde a ningún dato.';
  }
  if (delBackend && !esTecnico(delBackend)) return delBackend;
  if (delBackend) console.error('[http] detalle del error del servidor:', error.url, delBackend);

  switch (error.status) {
    case 0:
      return 'No se pudo contactar al servidor. Revisá la conexión.';
    case 400:
      return 'El pedido tiene datos inválidos.';
    case 403:
      return 'No tenés permiso para hacer esto.';
    case 404:
      return 'No se encontró lo que se pidió.';
    case 409:
      return 'El dato ya existe o cambió mientras lo editabas.';
    case 414:
      return 'El filtro es demasiado largo para la URL.';
    case 502:
    case 503:
    case 504:
      return 'El servidor no está respondiendo. Volvé a intentar en un momento.';
    default:
      return `Falló el pedido al servidor (${error.status}).`;
  }
}

/**
 * Avisa de los errores que nadie más maneja.
 *
 * **Nunca los traga**: siempre vuelve a lanzar. Un `catch` que termina en un
 * valor por defecto silencioso es exactamente lo que hizo que el mapa de alarmas
 * de IRIX mostrara "Con eventos abiertos: 0" habiendo 2.172 abiertos.
 *
 * El 401 no se notifica: lo resuelve `authInterceptor` refrescando el token, y
 * el usuario no tiene por qué enterarse.
 */
export const erroresInterceptor: HttpInterceptorFn = (req, next) => {
  const notificaciones = inject(NotificacionesService);

  return next(req).pipe(
    catchError((error: unknown) => {
      const silenciado = req.context.get(SIN_NOTIFICAR_ERROR);
      const esAuth = error instanceof HttpErrorResponse && error.status === 401;

      if (error instanceof HttpErrorResponse && !silenciado && !esAuth) {
        notificaciones.error(mensajeDe(error));
      }
      return throwError(() => error);
    }),
  );
};
